import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import axios from "axios";
import DateRangeSelector from "./DateRangeSelector";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-p400 p-2 border border-n200 shadow-md rounded text-xs">
        <p className="font-medium">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.name}: {parseFloat(entry.value).toLocaleString()} Cr
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const FiiDiiChartComponent = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [minDate, setMinDate] = useState(new Date());
  const [maxDate, setMaxDate] = useState(new Date());

  const apiURL =
    "https://wjurioweg2.execute-api.ap-south-1.amazonaws.com/default/fiiDiiData";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(apiURL);
        const rawData = response.data;

        // Process and format data for the chart
        const processedData = rawData.map((item) => {
          const dateParts = item.date.split("-");
          // Handle date format (assuming dd-MMM-yyyy)
          const dateObj = new Date(
            parseInt(dateParts[2]),
            getMonthNumber(dateParts[1]),
            parseInt(dateParts[0])
          );

          return {
            date: item.date,
            dateObj: dateObj,
            fiiNetInvestment: parseFloat(item.fii_equitynetinvestment || 0),
            diiNetInvestment: parseFloat(item.dii_equitynetinvestment || 0),
          };
        });

        // Sort by date in ascending order (similar to Flutter implementation)
        processedData.sort((a, b) => a.dateObj - b.dateObj);

        setData(processedData);

        // Set date range
        if (processedData.length > 0) {
          const earliestDate = processedData[0].dateObj;
          const latestDate = processedData[processedData.length - 1].dateObj;

          setMinDate(earliestDate);
          setMaxDate(latestDate);

          // Default to last 7 days (similar to Flutter implementation)
          const defaultStartDate = new Date(latestDate);
          defaultStartDate.setDate(defaultStartDate.getDate() - 7);

          setStartDate(
            defaultStartDate > earliestDate ? defaultStartDate : earliestDate
          );
          setEndDate(latestDate);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    // Filter data based on selected date range
    const filtered = data.filter((item) => {
      return item.dateObj >= startDate && item.dateObj <= endDate;
    });

    // Sort by date in ascending order
    filtered.sort((a, b) => a.dateObj - b.dateObj);

    setFilteredData(filtered);
  }, [data, startDate, endDate]);

  const getMonthNumber = (monthStr) => {
    const months = {
      Jan: 0,
      Feb: 1,
      Mar: 2,
      Apr: 3,
      May: 4,
      Jun: 5,
      Jul: 6,
      Aug: 7,
      Sep: 8,
      Oct: 9,
      Nov: 10,
      Dec: 11,
    };
    return months[monthStr];
  };

  const formatDateForDisplay = (date) => {
    const options = { day: "2-digit", month: "short", year: "numeric" };
    return date.toLocaleDateString("en-GB", options);
  };

  const handleDateChange = (newStartDate, newEndDate) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const getXAxisTicks = (data) => {
    if (!data || data.length === 0) return [];
    const total = data.length;

    // Always include first and last date
    const ticks = [data[0].date];
    if (total === 1) return ticks;

    // For 2-3 items, show all
    if (total <= 3) return data.map((d) => d.date);

    // Calculate step size for about 5 ticks, similar to Flutter implementation
    const step = Math.ceil(total / 5);

    for (let i = step; i < total - 1; i += step) {
      ticks.push(data[i].date);
    }

    // Always include the last date
    ticks.push(data[total - 1].date);

    return [...new Set(ticks)]; // Remove duplicates
  };

  return (
    <div className="bg-p400 rounded-lg shadow px-4 py-4 flex flex-col mb-8">
      <div className="flex justify-between items-start mb-4">
        <div className="mb-1">
          <h2 className="text-lg font-semibold text-p300">FII's Vs DII's</h2>
          <p className="text-sm font-medium text-n500">
            {startDate && endDate
              ? `${formatDateForDisplay(startDate)} - ${formatDateForDisplay(endDate)}`
              : "Loading date range..."}
          </p>
        </div>

        {minDate && maxDate && (
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateChange}
            minDate={minDate}
            maxDate={maxDate}
          />
        )}
      </div>

      <div className="h-72 w-full">
        {filteredData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={filteredData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => value.substring(0, 6)}
                ticks={getXAxisTicks(filteredData)}
                interval={0}
              />
              <YAxis hide={true} />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="linear"
                dataKey="fiiNetInvestment"
                name="FII Net"
                stroke="#3F59EF"
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 6 }}
              />
              <Line
                type="linear"
                dataKey="diiNetInvestment"
                name="DII Net"
                stroke="#00C5E4"
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center">
            <p className="text-n500">Loading chart data...</p>
          </div>
        )}
      </div>

      <div className="flex items-center mt-4 space-x-6">
        <div className="flex items-center">
          <span className="inline-block w-3 h-3 bg-[#3F59EF] rounded-full mr-2"></span>
          <span className="text-sm font-medium text-p300">FII</span>
        </div>
        <div className="flex items-center">
          <span className="inline-block w-3 h-3 bg-[#00C5E4] rounded-full mr-2"></span>
          <span className="text-sm font-medium text-p300">DII</span>
        </div>
      </div>
    </div>
  );
};

export default FiiDiiChartComponent;
